import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Plus, X, Shirt } from 'lucide-react';
import {
  buildSizeTableHtml,
  collectSizeTableSizes,
  getMeasurementKeysWithValues,
  resolveSizeGuideImage
} from '../lib/sizeTableHtml';

interface SizeTableGeneratorProps {
  sizes: Record<string, number>;
  productName?: string;
  onClose?: () => void;
  onSave?: (data: {
    productType: string;
    measurements: Record<string, Record<string, string>>;
    htmlCode: string;
  }) => void;
  required?: boolean;
  existingData?: {
    productType: string;
    measurements: Record<string, Record<string, string>>;
    htmlCode: string;
  } | null;
}

// Üst giyim ürün tipleri
const ustGiyimTipleri = [
  { name: 'Sweat', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/sweat.png' },
  { name: 'Gömlek', image: resolveSizeGuideImage('Gömlek') },
  { name: 'Ceket', image: resolveSizeGuideImage('Ceket') },
  { name: 'Body', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/body.png' },
  { name: 'Bluz', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/bluz.png' },
  { name: 'Yelek', image: resolveSizeGuideImage('Yelek') },
  { name: 'T-shirt', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/tshirt.png' },
  { name: 'Trençkot', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/trenc.png' },
  { name: 'Mont', image: resolveSizeGuideImage('Mont') },
  { name: 'Kazak', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/kazak.png' },
  { name: 'Kaban', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/kaban.png' },
  { name: 'Elbise', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/elbise.png' },
  { name: 'Elbise 2', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/elbise2.png' },
  { name: 'Büstiyer', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/bustiyer.png' },
  { name: 'Mayo', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/mayo.png' },
  { name: 'Bikini', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/bikini.png' }
];

// Alt giyim ürün tipleri
const altGiyimTipleri = [
  { name: 'Etek', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/etek.png' },
  { name: 'Şort', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/sort.png' },
  { name: 'Pantolon', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/pantolon.png' },
  { name: 'Uzun Etek', image: 'https://static.ticimax.cloud/10821/uploads/editoruploads/bedenmodeller/uzunetek.png' }
];

// Tüm ürün tipleri birleştirilmiş
const productTypes = [...ustGiyimTipleri, ...altGiyimTipleri];

// Alt giyim ürün adları (kontrol için)
const altGiyimAdlari = altGiyimTipleri.map(t => t.name);
// Mayo/Bikini gibi yüzme ürünleri: 3. ölçü "Boy" yerine "Basen" olur
const swimwearAdlari = ['Mayo', 'Bikini'];
const standardMeasurementKeys = ['gogus', 'bel', 'basen', 'boy'];

const getDefaultMeasurementKeys = (productName?: string) => {
  if (productName && altGiyimAdlari.includes(productName)) return ['bel', 'basen', 'boy'];
  if (productName && swimwearAdlari.includes(productName)) return ['gogus', 'bel', 'basen'];
  return ['gogus', 'bel', 'boy'];
};

const createEmptyMeasurements = (keys: string[]) =>
  keys.reduce((acc, key) => {
    acc[key] = {};
    return acc;
  }, {} as Record<string, Record<string, string>>);

interface Measurement {
  id: string;
  name: string;
  values: Record<string, string>;
}

// CSS for preview
const previewStyles = `
.BodyTableModal {
    max-width: 900px;
    padding: 0;
    background-color: #fff;
    width: 100%;
    border-radius: 10px;
    text-align: left;
}
.BodyTableModal .container-fluid {
    padding: 0;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    color: #212529;
}
.BodyTableModal .BodyTableLeft {
    padding: 0 30px 20px;
    background: #fdfdfd;
    flex: 0 0 100%;
    max-width: 100%;
}
.BodyTableModal .BodyTableRight {
    padding: 40px 30px;
    flex: 0 0 100%;
    max-width: 100%;
}
.BodyTableModal .BodyTableLeft .BodyHead {
    text-align: center;
    margin-bottom: 20px;
    padding-top: 20px;
}
.BodyTableModal .BodyTableLeft .BodyHead > .T {
    font-size: 22px;
    font-weight: 600;
}
.BodyTableModal .BodyTableLeft .BodyHead > .sT {
    font-size: 12px;
    font-weight: 500;
    line-height: 17px;
}
.BodyTableModal .BodyTableLeft .BodyHead > .sT ul {
    list-style: none;
    padding: 0;
    margin: 5px 0 0 0;
}
.BodyTableModal .BodyTableLeft .BodyHead > .sT li {
    display: inline;
}
.BodyTableModal .BodyTableLeft .BodyContent > .BodyAccordion > .item.Active > .accordion-content {
    display: block;
}
.BodyTableModal .BodyTableLeft .BodyContent > .BodyAccordion > .item > .accordion-content > table {
    width: 100%;
    text-align: center;
    margin-bottom: 10px;
}
.BodyTableModal .BodyTableLeft .BodyContent > .BodyAccordion > .item > .accordion-content > table thead tr th {
    background-color: #f1f3f2;
    padding: 8px;
    color: #000;
    font-size: 14px;
    font-weight: 600;
}
.BodyTableModal .BodyTableLeft .BodyContent > .BodyAccordion > .item > .accordion-content > table tbody tr td {
    padding: 5px 0;
    border-bottom: 1px solid #eee;
    border-right: 1px solid #eee;
    background-color: #fff;
    font-size: 14px;
}
.BodyTableModal .BodyTableLeft .BodyContent > .BodyAccordion > .item > .accordion-content > table tbody tr td:first-child {
    background-color: #f1f3f2;
    font-weight: 600;
}
.BodyTableModal .BodyTableRight .T {
    text-align: center;
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
}
.BodyTableModal .BodyTableRight .RightInfo.Show {
    display: block;
}
.BodyTableModal .BodyTableRight .RightInfo p {
    font-size: 12px;
    text-align: center;
    line-height: 16px;
    font-weight: 600;
}
.BodyTableModal .BodyTableRight .RightInfo img {
    display: table;
    margin: 50px auto;
    max-width: 200px;
}
.BodyTableModal .BodyTableRight .RightInfo .InfoFeatures > .item {
    text-align: left;
    display: flex;
    margin-bottom: 22px;
}
.BodyTableModal .BodyTableRight .RightInfo .InfoFeatures > .item > span {
    flex: none;
    width: 35px;
    height: 35px;
    background-color: #000;
    border-radius: 50%;
    line-height: 35px;
    text-align: center;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    margin-right: 14px;
}
.BodyTableModal .BodyTableRight .RightInfo .InfoFeatures > .item > .txt > .T {
    text-align: left;
    font-size: 14px;
    margin-bottom: 3px;
}
.BodyTableModal .BodyTableRight .RightInfo .InfoFeatures > .item > .txt > p {
    font-size: 12px;
    line-height: 16px;
    text-align: left;
    font-weight: normal;
}
.bedenImg {
    float: left;
    width: 50%;
    border-right: 1px solid #e9e9e9;
}
.InfoFeatures {
    float: right;
    width: 50%;
    padding-left: 30px;
    text-align: left;
}
.bedenWrap {
    float: left;
    width: 100%;
    display: flex;
    align-items: center;
    margin-top: 30px;
}
`;

const SizeTableGenerator: React.FC<SizeTableGeneratorProps> = ({ sizes, productName, onClose, onSave, required = false, existingData }) => {
  const [selectedProduct, setSelectedProduct] = useState<typeof productTypes[0] | null>(
    existingData ? productTypes.find(p => p.name === existingData.productType) || null : null
  );
  const [measurements, setMeasurements] = useState<Record<string, Record<string, string>>>({
    gogus: {},
    bel: {},
    boy: {}
  });
  const [additionalMeasurements, setAdditionalMeasurements] = useState<Measurement[]>([]);
  const [generatedHTML, setGeneratedHTML] = useState('');
  
  // Beden ekleme/kaldırma için state'ler
  const [customSizes, setCustomSizes] = useState<string[]>([]);
  const [newSizeInput, setNewSizeInput] = useState('');
  
  // Ölçü başlıklarını kaldırma özelliği
  const [enabledMeasurements, setEnabledMeasurements] = useState<Set<string>>(
    new Set(['gogus', 'bel', 'boy', 'basen'])
  );
  
  // Yeni ölçü başlığı ekleme
  const [newMeasurementInput, setNewMeasurementInput] = useState('');

  // Alt giyim mi kontrol et
  const isAltGiyim = selectedProduct ? altGiyimAdlari.includes(selectedProduct.name) : false;
  // Mayo/Bikini (üst giyim swimwear): 3. ölçü Boy yerine Basen
  const isSwimwear = !isAltGiyim && (selectedProduct ? swimwearAdlari.includes(selectedProduct.name) : false);

  const resetMeasurementsForProduct = (product: typeof productTypes[0]) => {
    const defaultKeys = getDefaultMeasurementKeys(product.name);
    setMeasurements(createEmptyMeasurements(defaultKeys));
    setEnabledMeasurements(new Set(defaultKeys));
    setAdditionalMeasurements([]);
    setGeneratedHTML('');
  };

  const handleSelectProduct = (product: typeof productTypes[0]) => {
    const productChanged = selectedProduct?.name !== product.name;
    setSelectedProduct(product);

    if (productChanged) {
      resetMeasurementsForProduct(product);
    }
  };

  const handleClear = () => {
    setMeasurements({});
    setAdditionalMeasurements([]);
    setEnabledMeasurements(new Set());
    setSelectedProduct(null);
    setGeneratedHTML('');
    setCustomSizes([]);
    setNewSizeInput('');
    setNewMeasurementInput('');
  };
  
  // Ölçü başlığını kaldır
  const handleRemoveMeasurement = (measurementKey: string) => {
    setEnabledMeasurements(prev => {
      const newSet = new Set(prev);
      newSet.delete(measurementKey);
      return newSet;
    });
    setMeasurements(prev => {
      if (!Object.prototype.hasOwnProperty.call(prev, measurementKey)) {
        return prev;
      }

      const { [measurementKey]: removedMeasurement, ...remainingMeasurements } = prev;
      void removedMeasurement;
      return remainingMeasurements;
    });
  };
  
  // Ölçü başlığını geri ekle
  const handleAddMeasurement = (measurementKey: string) => {
    setEnabledMeasurements(prev => new Set([...prev, measurementKey]));
    setMeasurements(prev => (
      prev[measurementKey] ? prev : { ...prev, [measurementKey]: {} }
    ));
  };
  
  // Yeni özel ölçü başlığı ekle
  const handleAddCustomMeasurement = () => {
    const trimmed = newMeasurementInput.trim();
    const measurementKey = trimmed.toLowerCase();

    if (trimmed && !enabledMeasurements.has(measurementKey)) {
      setEnabledMeasurements(prev => new Set([...prev, measurementKey]));
      setMeasurements(prev => ({ ...prev, [measurementKey]: {} }));
      setNewMeasurementInput('');
    }
  };

  // Props'tan gelen bedenler + custom bedenler birleştirilmiş
  const allSizes = useMemo(() => {
    return collectSizeTableSizes([...Object.keys(sizes || {}), ...customSizes], measurements, additionalMeasurements);
  }, [sizes, customSizes, measurements, additionalMeasurements]);

  // Beden ekle
  const handleAddSize = () => {
    const trimmed = newSizeInput.trim().toUpperCase();
    if (trimmed && !allSizes.includes(trimmed)) {
      setCustomSizes(prev => [...prev, trimmed]);
      setNewSizeInput('');
    }
  };

  // Beden kaldır (sadece custom olanlar kaldırılabilir)
  const handleRemoveSize = (size: string) => {
    if (customSizes.includes(size)) {
      setCustomSizes(prev => prev.filter(s => s !== size));
      // Measurements'tan da temizle
      setMeasurements(prev => {
        const newMeas = { ...prev };
        Object.keys(newMeas).forEach(key => {
          if (newMeas[key]) {
            const updated = { ...newMeas[key] };
            delete updated[size];
            newMeas[key] = updated;
          }
        });
        return newMeas;
      });
    }
  };

  // Başlangıç değerlerini ayarla
  useEffect(() => {
    if (existingData?.measurements) {
      setSelectedProduct(productTypes.find(p => p.name === existingData.productType) || null);

      // Mevcut veriyi yükle
      const { gogus = {}, bel = {}, boy = {}, basen = {}, ...additionalMeas } = existingData.measurements;

      // Tüm standart ölçü slotlarını koru — hangisinin gösterileceğini/kaydedileceğini
      // enabledMeasurements + render kapıları (isAltGiyim/isSwimwear) zaten belirliyor.
      // (Eski "basen varsa alt giyim {bel,basen,boy}" sezgisi, swimwear'in
      //  {gogus,bel,basen} şeklinde gogus'u düşürüp göğüs verisini siliyordu.)
      setMeasurements({ gogus, bel, basen, boy });

      const activeStandardMeasurements = getMeasurementKeysWithValues({ gogus, bel, boy, basen });
      if (activeStandardMeasurements.length > 0) {
        setEnabledMeasurements(new Set(activeStandardMeasurements));
      } else {
        setEnabledMeasurements(new Set(getDefaultMeasurementKeys(existingData.productType)));
      }
      
      // Ek ölçüleri yükle
      const additionalList: Measurement[] = Object.entries(additionalMeas).map(([name, values], index) => ({
        id: Date.now().toString() + index,
        name,
        values: values as Record<string, string>
      }));
      setAdditionalMeasurements(additionalList);
      setGeneratedHTML(existingData.htmlCode || '');
      setCustomSizes([]);
      setNewSizeInput('');
      setNewMeasurementInput('');
    }
  }, [existingData]);

  const handleMeasurementChange = (type: string, size: string, value: string) => {
    setMeasurements(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [size]: value
      }
    }));
  };

  const addAdditionalMeasurement = () => {
    const newMeasurement: Measurement = {
      id: Date.now().toString(),
      name: '',
      values: {}
    };
    setAdditionalMeasurements([...additionalMeasurements, newMeasurement]);
  };

  const removeAdditionalMeasurement = (id: string) => {
    setAdditionalMeasurements(additionalMeasurements.filter(m => m.id !== id));
  };

  const updateAdditionalMeasurement = (id: string, field: 'name' | string, value: string) => {
    setAdditionalMeasurements(prev => prev.map(m => {
      if (m.id === id) {
        if (field === 'name') {
          return { ...m, name: value };
        } else {
          return { ...m, values: { ...m.values, [field]: value } };
        }
      }
      return m;
    }));
  };

  const currentHTML = useMemo(() => {
    if (!selectedProduct) {
      return '';
    }

    return buildSizeTableHtml({
      sizes: allSizes,
      measurements,
      additionalMeasurements,
      enabledMeasurements,
      isAltGiyim,
      productName: selectedProduct.name,
      productImage: selectedProduct.image
    });
  }, [selectedProduct, allSizes, measurements, additionalMeasurements, enabledMeasurements, isAltGiyim]);

  const generateHTML = () => {
    if (!selectedProduct || !currentHTML) {
      alert('Lütfen bir ürün tipi seçin');
      return;
    }

    setGeneratedHTML(currentHTML);
  };

  const handleSave = () => {
    if (!selectedProduct || !currentHTML) {
      alert('Lütfen bir ürün tipi seçin');
      return;
    }

    const productStandardMeasurements = getDefaultMeasurementKeys(selectedProduct.name);
    const activeMeasurements = Object.entries(measurements).reduce((acc, [key, values]) => {
      const isStandardMeasurement = standardMeasurementKeys.includes(key);

      if (
        enabledMeasurements.has(key) &&
        (!isStandardMeasurement || productStandardMeasurements.includes(key))
      ) {
        acc[key] = values;
      }

      return acc;
    }, {} as Record<string, Record<string, string>>);

    const allMeasurements = {
      ...activeMeasurements,
      ...additionalMeasurements.reduce((acc, m) => {
        const measurementName = m.name.trim();

        if (measurementName) {
          acc[measurementName] = m.values;
        }
        return acc;
      }, {} as Record<string, Record<string, string>>)
    };

    if (onSave) {
      onSave({
        productType: selectedProduct.name,
        measurements: allMeasurements,
        htmlCode: currentHTML
      });
    }

    setGeneratedHTML(currentHTML);

    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-[95vw] h-[95vh] max-w-[1600px] overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shirt className="w-5 h-5" />
            Beden Tablosu Oluşturucu
            {productName && <Badge variant="outline">{productName}</Badge>}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="overflow-y-auto max-h-[calc(95vh-80px)]">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Sol Panel */}
            <div className="space-y-4">
              {/* Ürün Tipi Seçimi - Üst Giyim */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Üst Giyim</h3>
                <div className="grid grid-cols-5 gap-2">
                  {ustGiyimTipleri.map(type => (
                    <button
                      key={type.name}
                      onClick={() => handleSelectProduct(type)}
                      className={`relative p-3 border-2 rounded-md transition-all text-center ${
                        selectedProduct?.name === type.name 
                          ? 'border-black bg-gray-100' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      {selectedProduct?.name === type.name && (
                        <div className="absolute top-1 right-1 w-4 h-4 bg-black text-white rounded-full flex items-center justify-center text-xs">
                          ✓
                        </div>
                      )}
                      <div className="text-xs font-medium">{type.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Ürün Tipi Seçimi - Alt Giyim */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Alt Giyim</h3>
                <div className="grid grid-cols-4 gap-2">
                  {altGiyimTipleri.map(type => (
                    <button
                      key={type.name}
                      onClick={() => handleSelectProduct(type)}
                      className={`relative p-3 border-2 rounded-md transition-all text-center ${
                        selectedProduct?.name === type.name 
                          ? 'border-blue-600 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      {selectedProduct?.name === type.name && (
                        <div className="absolute top-1 right-1 w-4 h-4 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">
                          ✓
                        </div>
                      )}
                      <div className="text-xs font-medium">{type.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Beden Ölçüleri */}
              <div>
                <h3 className="text-sm font-semibold mb-3">
                  Beden Ölçüleri 
                  <Badge variant="secondary" className="ml-2">{allSizes.length} beden</Badge>
                  {isAltGiyim && <Badge variant="outline" className="ml-2 text-blue-600 border-blue-600">Alt Giyim</Badge>}
                </h3>
                
                <div className="space-y-3 bg-gray-50 p-4 rounded-md">
                  {/* Beden Ekleme */}
                  <div className="flex gap-2 items-center pb-2 border-b">
                    <input
                      type="text"
                      value={newSizeInput}
                      onChange={(e) => setNewSizeInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddSize()}
                      className="flex-1 px-3 py-1.5 text-sm border rounded"
                      placeholder="Yeni beden ekle (örn: 42, XL)"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddSize}
                      disabled={!newSizeInput.trim()}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ekle
                    </Button>
                  </div>

                  {/* Başlık */}
                  <div className="grid grid-cols-[100px_repeat(auto-fill,minmax(60px,1fr))] gap-2">
                    <div className="text-xs font-semibold">Ölçü</div>
                    {allSizes.map(size => (
                      <div key={size} className="text-xs font-semibold text-center flex items-center justify-center gap-1">
                        <span>{size}</span>
                        {customSizes.includes(size) && (
                          <button
                            onClick={() => handleRemoveSize(size)}
                            className="text-red-500 hover:text-red-700"
                            title="Bu bedeni kaldır"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Ölçü Başlıkları Yönetimi */}
                  <div className="flex flex-wrap gap-2 pb-2 border-b mb-2">
                    <span className="text-xs text-gray-500">Aktif Ölçüler:</span>
                    {!enabledMeasurements.has('gogus') && !isAltGiyim && (
                      <Badge 
                        variant="outline" 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleAddMeasurement('gogus')}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Göğüs
                      </Badge>
                    )}
                    {!enabledMeasurements.has('bel') && (
                      <Badge 
                        variant="outline" 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleAddMeasurement('bel')}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Bel
                      </Badge>
                    )}
                    {!enabledMeasurements.has('basen') && (isAltGiyim || isSwimwear) && (
                      <Badge
                        variant="outline"
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleAddMeasurement('basen')}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Basen
                      </Badge>
                    )}
                    {!enabledMeasurements.has('boy') && !isSwimwear && (
                      <Badge
                        variant="outline"
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleAddMeasurement('boy')}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Boy
                      </Badge>
                    )}
                    <div className="flex gap-1 ml-auto">
                      <input
                        type="text"
                        value={newMeasurementInput}
                        onChange={(e) => setNewMeasurementInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCustomMeasurement()}
                        className="px-2 py-0.5 text-xs border rounded w-24"
                        placeholder="Özel ölçü"
                      />
                      <Button variant="ghost" size="sm" className="h-6 px-2" onClick={handleAddCustomMeasurement}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Üst Giyim: Göğüs */}
                  {!isAltGiyim && enabledMeasurements.has('gogus') && (
                    <div className="grid grid-cols-[100px_repeat(auto-fill,minmax(60px,1fr))] gap-2">
                      <div className="text-sm flex items-center gap-1">
                        <button 
                          onClick={() => handleRemoveMeasurement('gogus')}
                          className="text-red-500 hover:text-red-700"
                          title="Bu ölçüyü kaldır"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        Göğüs
                      </div>
                      {allSizes.map(size => (
                        <input
                          key={size}
                          type="text"
                          value={measurements.gogus?.[size] || ''}
                          onChange={(e) => handleMeasurementChange('gogus', size, e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded"
                          placeholder="cm"
                        />
                      ))}
                    </div>
                  )}

                  {/* Bel - her iki tip için */}
                  {enabledMeasurements.has('bel') && (
                    <div className="grid grid-cols-[100px_repeat(auto-fill,minmax(60px,1fr))] gap-2">
                      <div className="text-sm flex items-center gap-1">
                        <button 
                          onClick={() => handleRemoveMeasurement('bel')}
                          className="text-red-500 hover:text-red-700"
                          title="Bu ölçüyü kaldır"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        Bel
                      </div>
                      {allSizes.map(size => (
                        <input
                          key={size}
                          type="text"
                          value={measurements.bel?.[size] || ''}
                          onChange={(e) => handleMeasurementChange('bel', size, e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded"
                          placeholder="cm"
                        />
                      ))}
                    </div>
                  )}

                  {/* Alt Giyim + Mayo/Bikini: Basen */}
                  {(isAltGiyim || isSwimwear) && enabledMeasurements.has('basen') && (
                    <div className="grid grid-cols-[100px_repeat(auto-fill,minmax(60px,1fr))] gap-2">
                      <div className="text-sm flex items-center gap-1">
                        <button 
                          onClick={() => handleRemoveMeasurement('basen')}
                          className="text-red-500 hover:text-red-700"
                          title="Bu ölçüyü kaldır"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        Basen
                      </div>
                      {allSizes.map(size => (
                        <input
                          key={size}
                          type="text"
                          value={measurements.basen?.[size] || ''}
                          onChange={(e) => handleMeasurementChange('basen', size, e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded"
                          placeholder="cm"
                        />
                      ))}
                    </div>
                  )}

                  {/* Boy - alt giyim + normal üst giyim (swimwear hariç) */}
                  {enabledMeasurements.has('boy') && !isSwimwear && (
                    <div className="grid grid-cols-[100px_repeat(auto-fill,minmax(60px,1fr))] gap-2">
                      <div className="text-sm flex items-center gap-1">
                        <button 
                          onClick={() => handleRemoveMeasurement('boy')}
                          className="text-red-500 hover:text-red-700"
                          title="Bu ölçüyü kaldır"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        Boy
                      </div>
                      {allSizes.map(size => (
                        <input
                          key={size}
                          type="text"
                          value={measurements.boy?.[size] || ''}
                          onChange={(e) => handleMeasurementChange('boy', size, e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded"
                          placeholder="cm"
                        />
                      ))}
                    </div>
                  )}
                  
                  {/* Özel Ölçüler (enabledMeasurements'tan gelen ama standart olmayan) */}
                  {Array.from(enabledMeasurements)
                    .filter(m => !['gogus', 'bel', 'basen', 'boy'].includes(m))
                    .map(measureKey => (
                      <div key={measureKey} className="grid grid-cols-[100px_repeat(auto-fill,minmax(60px,1fr))] gap-2">
                        <div className="text-sm flex items-center gap-1">
                          <button 
                            onClick={() => handleRemoveMeasurement(measureKey)}
                            className="text-red-500 hover:text-red-700"
                            title="Bu ölçüyü kaldır"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          {measureKey.charAt(0).toUpperCase() + measureKey.slice(1)}
                        </div>
                        {allSizes.map(size => (
                          <input
                            key={size}
                            type="text"
                            value={measurements[measureKey]?.[size] || ''}
                            onChange={(e) => handleMeasurementChange(measureKey, size, e.target.value)}
                            className="w-full px-2 py-1 text-xs border rounded"
                            placeholder="cm"
                          />
                        ))}
                      </div>
                    ))}

                  {/* Ek Ölçüler */}
                  {additionalMeasurements.map(measure => (
                    <div key={measure.id} className="grid grid-cols-[100px_repeat(auto-fill,minmax(60px,1fr))] gap-2">
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={measure.name}
                          onChange={(e) => updateAdditionalMeasurement(measure.id, 'name', e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded"
                          placeholder="Ölçü adı"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeAdditionalMeasurement(measure.id)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      {allSizes.map(size => (
                        <input
                          key={size}
                          type="text"
                          value={measure.values[size] || ''}
                          onChange={(e) => updateAdditionalMeasurement(measure.id, size, e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded"
                          placeholder="cm"
                        />
                      ))}
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addAdditionalMeasurement}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Yeni Ölçü Ekle
                  </Button>
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex gap-2">
                <Button 
                  onClick={generateHTML}
                  className="flex-1"
                  disabled={!selectedProduct}
                >
                  Beden Tablosu Oluştur
                </Button>
                {selectedProduct && (
                  <Button 
                    onClick={handleSave}
                    variant="default"
                  >
                    Kaydet ve Devam Et
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={handleClear}
                >
                  Temizle
                </Button>
              </div>
            </div>

            {/* Sağ Panel - Önizleme */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Önizleme</h3>
              
              {(currentHTML || generatedHTML) ? (
                <>
                  <style dangerouslySetInnerHTML={{ __html: previewStyles }} />
                  <div 
                    className="border rounded-md overflow-auto"
                    style={{ maxHeight: 'calc(95vh - 180px)' }}
                    dangerouslySetInnerHTML={{ 
                      __html: (currentHTML || generatedHTML).replace('style="display: none;"', '')
                    }}
                  />
                </>
              ) : (
                <div className="border rounded-md p-12 text-center text-gray-400">
                  <Shirt className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Beden tablosu önizlemesi burada görünecek</p>
                  <p className="text-sm mt-2">Önce ürün tipi seçin ve HTML oluşturun</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SizeTableGenerator;
